import React, { useEffect, useRef, useState } from 'react';
import { InvestigationDocuments, RequestedInvestigationDocument } from '@tradingblock/types';
import { Button, FormGroup, Text, Loading, Modal } from '@tradingblock/components';
import { FileError, useDropzone } from 'react-dropzone';
import { useCallback } from 'react';
import jsPDF from 'jspdf';
import { ApplicationModel } from '../../types';
import { addPrimaryPhotoBlob, addSecondaryPhotoBlob } from '../../services/Data';
import { Modal as BootstrapModal, Button as RButton } from 'react-bootstrap';
import _ from 'lodash';
import { useApi } from '../../context';

/**
 * OptionalUpload component properties
 *
 * @property {RequestedInvestigationDocument} requestedInvestigationDocument Requested document infomation.
 * @property {number} accountId The account ID for the upload.
 * @property {boolean} primary Boolean if user is primary or secondary
 */
export interface InvestigationUploadProps {
  requestedInvestigationDocument: RequestedInvestigationDocument;
  primary: boolean;
  setValues: (values: ApplicationModel) => void;
  values: ApplicationModel;
  accountId: number | undefined;
}

/**
 * An independent upload widget for uploading misc documents
 *
 * @param {InvestigationUploadProps} props
 * @returns {JSX.Element}
 */
export default function OptionalUpload({
  requestedInvestigationDocument,
  primary,
  setValues,
  values,
  accountId,
}: InvestigationUploadProps) {
  if (requestedInvestigationDocument.requestedDocuments.length <= 0) {
    throw new Error('Need one requested document to process');
  }
  const api = useApi();

  // Used states
  const [errors, setErrors] = useState<FileError[]>([]);
  const [selectedDocumentName, setSelectedDocumentName] = useState<InvestigationDocuments>(
    requestedInvestigationDocument.requestedDocuments[0]
  );
  const [needToSelectDocumentName, setNeedToSelectDocumentName] = useState<boolean>(
    requestedInvestigationDocument.requestedDocuments.length > 1
  );
  const [frontImage, setFrontImage] = useState<File | null>(null);
  const [backImage, setBackImage] = useState<File | null>(null);
  const [tempBlob, setTempBlob] = useState<Blob | null>(null);

  const renderImageToCanvas = async (imageSrc: any) => {
    const img = new Image();
    img.src = imageSrc;
    await new Promise(resolve => (img.onload = resolve));
    const canvas = document.createElement('canvas');
    canvas.width = img.width;
    canvas.height = img.height;
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.drawImage(img, 0, 0);
    }
    return canvas;
  };

  const generatePDF = async () => {
    try {
      const pdf = new jsPDF();

      // Render front image to canvas
      if (frontImage) {
        const frontImageURL = URL.createObjectURL(frontImage);
        const frontCanvas = await renderImageToCanvas(frontImageURL);
        const frontImgData = frontCanvas.toDataURL('image/jpeg');
        const frontImgWidth = frontCanvas.width;
        const frontImgHeight = frontCanvas.height;

        // Maintain aspect ratio for front image
        const frontAspectRatio = frontImgWidth / frontImgHeight;
        const frontNewWidth = 150; // Desired width
        const frontNewHeight = frontNewWidth / frontAspectRatio;

        pdf.addImage(frontImgData, 'JPEG', 15, 40, frontNewWidth, frontNewHeight);
      }

      if (backImage) {
        // Add a new page
        pdf.addPage();
        // Render back image to canvas
        const backImageURL = URL.createObjectURL(backImage);
        const backCanvas = await renderImageToCanvas(backImageURL);
        const backImgData = backCanvas.toDataURL('image/jpeg');
        const backImgWidth = backCanvas.width;
        const backImgHeight = backCanvas.height;

        // Maintain aspect ratio for back image
        const backAspectRatio = backImgWidth / backImgHeight;
        const backNewWidth = 150; // Desired width
        const backNewHeight = backNewWidth / backAspectRatio;
        pdf.addImage(backImgData, 'JPEG', 15, 40, backNewWidth, backNewHeight);
      }

      // Generate PDF Blob and display
      const pdfBlob = pdf.output('blob');

      // check size of the pdfBlob
      if (pdfBlob.size > maxFileSize) {
        alert('The combined image is too large to upload. Please try again with smaller images.');
        return;
      }

      // return a new file object with the pdfBlob
      return new File([pdf.output('blob')], 'combined.pdf', { type: 'application/pdf' });
    } catch (error) {
      console.error('Error generating PDF:', error);
    }
  };

  const handleCombinedUpload = async () => {
    if (frontImage && backImage) {
      try {
        const fileToUpload = await generatePDF();

        if (fileToUpload) {
          if (primary) setValues(addPrimaryPhotoBlob(values, { type: selectedDocumentName, file: fileToUpload }));
          else setValues(addSecondaryPhotoBlob(values, { type: selectedDocumentName, file: fileToUpload }));
          handleMiscUpload(accountId, { type: selectedDocumentName, file: fileToUpload }, primary);
          setErrors([]);
        } else {
          throw new Error('File to upload is undefined.');
        }
      } catch (error) {
        setErrors([{ message: 'An error occurred while processing the upload.', code: 'UPLOAD_ERROR' }]);
      }
    }
  };

  const handleWebcamUpload = async () => {
    try {
      const fileToUpload = await generatePDF();

      if (fileToUpload) {
        if (primary) setValues(addPrimaryPhotoBlob(values, { type: selectedDocumentName, file: fileToUpload }));
        else setValues(addSecondaryPhotoBlob(values, { type: selectedDocumentName, file: fileToUpload }));
        handleMiscUpload(accountId, { type: selectedDocumentName, file: fileToUpload }, primary);
        setOpenCameraModal(false);
        setErrors([]);
      } else {
        throw new Error('File to upload is undefined.');
      }
    } catch (error) {
      setErrors([{ message: 'An error occurred while processing the upload.', code: 'UPLOAD_ERROR' }]);
    }
  };

  async function handleMiscUpload(
    accountId: number | undefined,
    photoBlob: { type: InvestigationDocuments; file: File },
    primary: boolean
  ) {
    try {
      if (accountId) await api.application.uploadMiscDocument(photoBlob.file, accountId, primary, photoBlob.type);
    } catch (error) {
      console.error('Upload Failed: ', error);
    }
  }

  // maxFileSize variable set to 12MB
  const maxFileSize = 12 * 1024 * 1024;

  const isIdCard =
    selectedDocumentName === InvestigationDocuments.StateIdCard ||
    selectedDocumentName === InvestigationDocuments.DriversLicense;

  const handleImageDrop = (side: 'front' | 'back') => async (acceptedFiles: File[]) => {
    // if the side is front, set the front image state
    // if the side is back, set the back image state
    const file = acceptedFiles[0];
    // if both the front and back images are set, combine them into a single PDF and upload automatically without user intervention
    if (side === 'front') {
      setFrontImage(file);
    } else if (side === 'back') {
      setBackImage(file);
    }
  };

  // Dropzone properties for the upload input field
  const { getRootProps, getInputProps, acceptedFiles } = useDropzone({
    onDrop: () => {},
    onDropAccepted: acceptedFiles => {
      // If the files where accepted from frontend validation
      // The base on the type, upload the document to the backend
      if (primary) setValues(addPrimaryPhotoBlob(values, { type: selectedDocumentName, file: acceptedFiles[0] }));
      else setValues(addSecondaryPhotoBlob(values, { type: selectedDocumentName, file: acceptedFiles[0] }));
      handleMiscUpload(accountId, { type: selectedDocumentName, file: acceptedFiles[0] }, primary);
    },
    onDropRejected: fileRejections => {
      //Set the upload failed and the file errors to display
      setErrors(fileRejections[0].errors);
    },
    maxSize: maxFileSize,
    maxFiles: 1,
    accept: 'image/png, image/jpeg, image/jpg, image/gif, application/pdf, capture=camera',
  });

  const { getRootProps: getFrontRootProps, getInputProps: getFrontInputProps } = useDropzone({
    onDrop: acceptedFiles => handleImageDrop('front')(acceptedFiles),
    maxSize: maxFileSize,
    maxFiles: 1,
    accept: 'image/png, image/jpeg, image/jpg, image/gif, capture=camera',
  });

  const { getRootProps: getBackRootProps, getInputProps: getBackInputProps } = useDropzone({
    onDrop: acceptedFiles => handleImageDrop('back')(acceptedFiles),
    maxSize: maxFileSize,
    maxFiles: 1,
    accept: 'image/png, image/jpeg, image/jpg, image/gif, capture=camera',
  });

  const formatDocumentName = (documentName: string) => {
    const specialCases: { [key: string]: string } = {
      DriversLicense: "Driver's License",
      StateIdCard: 'State ID Card',
      Passport: 'Passport',
      MilitaryId: 'Military ID',
      SsnCard: 'SSN Card',
      SsaLetter: 'SSA Letter',
      IrsItinLetter: 'IRS ITIN Letter',
      OtherGovernmentId: 'Other Government ID',
      CddDocument: 'CDD Document',
      AllPassingCipResults: 'All Passing CIP Results',
      IrsW9Form: 'IRS W-9 Form',
      SignatureImage: 'Signature Image',
      IdDocument: 'ID Document',
      AffiliateApproval: 'Affiliate Approval',
      Document: 'Document',
      Other: 'Other',
      GovernmentId: 'Government ID',
    };

    if (specialCases[documentName]) {
      return specialCases[documentName];
    }

    return (
      documentName
        // Add spaces before each capital letter except the first one
        .replace(/([A-Z])/g, ' $1')
        // Trim any leading spaces
        .trim()
        // Convert to title case
        .replace(/\w\S*/g, txt => txt.charAt(0).toUpperCase() + txt.substring(1).toLowerCase())
    );
  };

  // we need to update the driver's license and state id card document uploads to require a front and back image
  // for RQD cleared accounts we can simply provide a new button to upload the back image separately
  // however for Apex accounts, they will not accept multiple uploads for the same document
  // as such, we need to combine the front and back images into a single PDF for upload

  useEffect(() => {
    if (frontImage && backImage && !openCameraModal) {
      handleCombinedUpload();
    }
  }, [frontImage, backImage]);

  // if a user has selected a document that requires a front and back image, and they have not uploaded both images, but they change the selected document, we should clear the images
  useEffect(() => {
    if (isIdCard && (frontImage || backImage)) {
      setFrontImage(null);
      setBackImage(null);
    }
  }, [selectedDocumentName]);

  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const photoRef = useRef<HTMLCanvasElement | null>(null);
  const frontPhotoRef = useRef<HTMLCanvasElement | null>(null);
  const backPhotoRef = useRef<HTMLCanvasElement | null>(null);
  const [hasPhoto, setHasPhoto] = useState(false);
  const [cameraLoaded, setCameraLoaded] = useState(false);
  const [cameraErrored, setCameraErrored] = useState(false);
  const [cameraExists, setCameraExists] = useState(false);
  const [cameraPermission, setCameraPermission] = useState(true);
  const [openCameraModal, setOpenCameraModal] = useState(false);
  const [loadingError, setLoadingError] = useState<string>('');
  const [isDisabledSubmit, setIsDisabledSubmit] = useState(true);

  // Function to check for available cameras
  const checkForCamera = async () => {
    try {
      if (navigator.mediaDevices && navigator.mediaDevices.enumerateDevices) {
        const devices = await navigator.mediaDevices.enumerateDevices();
        const hasVideoInput = devices.some(device => device.kind === 'videoinput');
        setCameraExists(hasVideoInput); // Update state based on presence of camera
      }
    } catch (error) {
      console.error('Error checking for camera:', error);
    }
  };

  async function checkCameraPermission() {
    try {
      const result = await navigator.permissions.query({ name: 'camera' });

      if (result.state === 'granted') {
        setCameraExists(true);
      } else if (result.state === 'denied') {
        setCameraExists(false);
        setCameraPermission(false);
      }

      // Listen for changes in permission state
      result.onchange = () => {
        if (result.state === 'denied') {
          setCameraExists(false);
          setCameraPermission(false);
        }
      };
    } catch (error) {
      console.log('Error checking camera permission:', error);
    }
  }

  useEffect(() => {
    checkForCamera();
    checkCameraPermission();
  }, []); // Runs once on component mount

  const currentTimeout = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (cameraLoaded) {
      clearTimeout(currentTimeout.current as NodeJS.Timeout);
    }
  }, [cameraLoaded, currentTimeout]);

  const viewPhoto = (side: string = 'front') => {
    const image = side === 'front' ? frontImage : backImage;
    if (image) {
      const imageUrl = URL.createObjectURL(image);

      const img = new Image();
      img.src = imageUrl;

      img.onload = () => {
        const canvas = side === 'front' ? frontPhotoRef.current : backPhotoRef.current;
        if (canvas) {
          const context = canvas.getContext('2d');
          // Calculate new dimensions to maintain aspect ratio
          let photowidth = 414;
          let photoheight = 414 / (1920 / 1080);

          // Set canvas size
          canvas.width = photowidth;
          canvas.height = photoheight;

          // Draw the image onto the canvas
          if (context) {
            context.imageSmoothingEnabled = true;
            context.imageSmoothingQuality = 'high';
            context.drawImage(img, 0, 0, 1920, 1080, 0, 0, photowidth, photoheight);
          }

          // Release the object URL (optional, for memory management)
          URL.revokeObjectURL(imageUrl);
        }
      };

      img.onerror = () => {
        console.error('Failed to load image.');
        URL.revokeObjectURL(imageUrl);
      };
      setHasPhoto(true);
    }
  };

  const getVideo = useCallback(
    async (counter: number = 3) => {
      try {
        checkCameraPermission();
        setCameraLoaded(false);
        setLoadingError('');

        streamRef.current = await navigator.mediaDevices.getUserMedia({
          video: {
            width: { ideal: 1920 },
            height: { ideal: 1080 },
            frameRate: { ideal: 30 },
          },
        });

        let video = videoRef.current;
        if (video && streamRef.current) {
          video.srcObject = streamRef.current;
          video.play();
          video.onloadeddata = () => {
            setCameraLoaded(true);
          };

          if (counter > 0) {
            currentTimeout.current = setTimeout(() => {
              console.log('Retrying media capture. Try Count: ', counter);
              getVideo(counter - 1);
              currentTimeout.current = null;
            }, 3000);
            return () => clearTimeout(currentTimeout.current as NodeJS.Timeout);
          } else {
            setCameraErrored(true);
            setCameraExists(false);
          }
        }
      } catch (err) {
        console.error(err);

        if (err.name === 'NotReadableError') {
          setLoadingError(
            'Webcam/Camera is already in use. To upload with Webcam/Camera, please reset the Webcam/Camera and try again.'
          );
        } else if ((counter > 0 && !(err.name === 'NotAllowedError')) || err.name === 'PermissionDeniedError') {
          currentTimeout.current = setTimeout(() => {
            console.log('Retrying media capture. Try Count: ', counter);
            getVideo(counter - 1);
            currentTimeout.current = null;
          }, 3000);
          return () => clearTimeout(currentTimeout.current as NodeJS.Timeout);
        } else {
          setCameraErrored(true);
          setCameraExists(false);
        }
      }
    },
    [cameraPermission]
  );

  function stopVideo() {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
    }
  }

  const takePhoto = () => {
    const width = 1920;
    const height = 1080;

    let video = videoRef.current;
    let photo = photoRef.current;

    if (video && photo) {
      photo.width = width;
      photo.height = height;

      let context = photo.getContext('2d');
      if (context) {
        // Enable smooth scaling
        context.imageSmoothingEnabled = true;
        context.imageSmoothingQuality = 'high';

        context.drawImage(video, 0, 0);
        setHasPhoto(true);

        photo.toBlob(blob => {
          if (blob) {
            setTempBlob(blob);
          }
        }, 'image/jpeg');

        photo.width = 414;
        photo.height = 414 / (1920 / 1080);

        context.drawImage(video, 0, 0, width, height, 0, 0, photo.width, photo.height);
      }
    }
  };

  const retakePhoto = (backPhoto = false) => {
    let photo = photoRef.current;
    if (photo) {
      let context = photo.getContext('2d');
      if (context) {
        context.clearRect(0, 0, photo.width, photo.height);
        setHasPhoto(false);
        if (!backPhoto) setFrontImage(null);
        else setBackImage(null);
      }
    }
  };

  const clearPhoto = (backPhoto = false) => {
    let photo = !backPhoto ? frontPhotoRef.current : backPhotoRef.current;
    if (photo) {
      let context = photo.getContext('2d');
      if (context) {
        context.clearRect(0, 0, photo.width, photo.height);
        setHasPhoto(false);
        if (!backPhoto) setFrontImage(null);
        else setBackImage(null);
      }
    }
  };

  const keepPhoto = (backPhoto = false) => {
    if (tempBlob) {
      const imageName = isIdCard
        ? !backPhoto
          ? 'front-' + selectedDocumentName + '.jpg'
          : 'back-' + selectedDocumentName + '.jpg'
        : selectedDocumentName + '.jpg';

      // Create a File object from the Blob
      if (!backPhoto) setFrontImage(new File([tempBlob], imageName, { type: 'image/jpeg' }));
      else setBackImage(new File([tempBlob], imageName, { type: 'image/jpeg' }));
      setHasPhoto(false);
      setTempBlob(null);
    }
  };

  useEffect(() => {
    if (!openCameraModal) stopVideo();
    else {
      setHasPhoto(false);
      setFrontImage(null);
      setBackImage(null);
      setIsDisabledSubmit(true);
    }
  }, [openCameraModal]);

  // if a user has selected a document that requires a front and back image, and they have not uploaded both images, but they change the selected document, we should clear the images
  useEffect(() => {
    if (isIdCard) {
      if (frontImage && backImage) {
        setIsDisabledSubmit(false);
        viewPhoto();
        viewPhoto('back');
      } else setIsDisabledSubmit(true);
    } else if (frontImage) {
      setIsDisabledSubmit(false);
      viewPhoto();
    } else setIsDisabledSubmit(true);
  }, [frontImage, backImage]);

  return (
    <FormGroup>
      <p>
        <span className="mute">
          <Text
            id={`investigationUpload.documents.${requestedInvestigationDocument.name}`}
            textKey={'description'}
            type={'field'}
          />
        </span>
      </p>
      <>
        {needToSelectDocumentName && (
          <>
            <strong>
              <Text id={'investigationUpload.selectDocumentEyebrow'} type={'field'} />
            </strong>
            <ul className={'checklist'}>
              {requestedInvestigationDocument.requestedDocuments.map(requestedDocumentOption => (
                <li key={`${requestedDocumentOption}_${primary ? '0' : '1'}`}>
                  <label>
                    <span className={'checkcontainer'}>
                      <input
                        type={'checkbox'}
                        name={`${requestedDocumentOption}_${primary ? '0' : '1'}`}
                        id={`${requestedDocumentOption}_${primary ? '0' : '1'}`}
                        value={requestedDocumentOption}
                        checked={requestedDocumentOption === selectedDocumentName}
                        onChange={() => setSelectedDocumentName(requestedDocumentOption)}
                      />
                      <span className={'radiobtn'}></span>
                    </span>
                    <Text id={`investigationUpload.documentOptions.${requestedDocumentOption}`} type={'field'} />
                  </label>
                </li>
              ))}
            </ul>
          </>
        )}

        {!isIdCard && (
          <div {...getRootProps()}>
            <input {...getInputProps()} />
            <Button className={'btn-btn btn-primary'}>
              {needToSelectDocumentName ? <Text id={'investigationUpload.selectUpload'} type={'field'} /> : ''}
              <Text
                id={'investigationUpload.upload'}
                type={'field'}
                data={{
                  documentName: formatDocumentName(selectedDocumentName),
                }}
              />
            </Button>
          </div>
        )}

        {isIdCard && (
          <>
            {/* Only display if there is no frontImage state currently set*/}
            {frontImage === null && (
              <div {...getFrontRootProps()}>
                <input {...getFrontInputProps()} />
                <Button className={'btn-btn btn-primary mt-2'}>
                  {needToSelectDocumentName ? <Text id={'investigationUpload.selectUpload'} type={'field'} /> : ''}
                  <Text
                    id={'investigationUpload.upload'}
                    type={'field'}
                    data={{
                      documentName: formatDocumentName(selectedDocumentName) + ' - Front',
                    }}
                  />
                </Button>
              </div>
            )}
            {/* Only display if there is no backImage state currently set*/}
            {backImage === null && (
              <div {...getBackRootProps()}>
                <input {...getBackInputProps()} />
                <Button className={'btn-btn btn-primary mt-2'}>
                  {needToSelectDocumentName ? <Text id={'investigationUpload.selectUpload'} type={'field'} /> : ''}
                  <Text
                    id={'investigationUpload.upload'}
                    type={'field'}
                    data={{
                      documentName: formatDocumentName(selectedDocumentName) + ' - Back',
                    }}
                  />
                </Button>
              </div>
            )}
          </>
        )}

        <br />

        {cameraExists && openCameraModal && (
          <>
            <Modal setShow={setOpenCameraModal} show={openCameraModal} aria-labelledby="cameraModal">
              <BootstrapModal.Header closeButton>
                <BootstrapModal.Title>Webcam Capture</BootstrapModal.Title>
              </BootstrapModal.Header>
              <BootstrapModal.Body>
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                  <b>Uploading {formatDocumentName(selectedDocumentName)}</b>
                </div>
                <div style={{ marginTop: '.5rem' }}>
                  <div className={!hasPhoto ? 'camera' : 'camera hidden'}>
                    {!cameraLoaded && (
                      <div
                        style={{
                          width: 414,
                          height: 414 / (16 / 9),
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        {loadingError ? <strong>{loadingError}</strong> : <Loading />}
                      </div>
                    )}
                    <video
                      ref={videoRef}
                      className="cameraCanvas"
                      style={cameraLoaded && isDisabledSubmit ? { display: 'block' } : { display: 'none' }}
                    ></video>
                    {isIdCard && cameraLoaded && (
                      <>
                        {!frontImage && (
                          <RButton variant={'info'} className="mt-2" onClick={takePhoto}>
                            Take Front Photo
                          </RButton>
                        )}
                        {frontImage && !backImage && (
                          <RButton variant={'info'} className="mt-2" onClick={takePhoto}>
                            Take Back Photo
                          </RButton>
                        )}
                      </>
                    )}
                    {!isIdCard && cameraLoaded && !frontImage && (
                      <RButton variant={'info'} className="mt-2" onClick={takePhoto}>
                        Take Photo
                      </RButton>
                    )}
                  </div>
                  <div className={hasPhoto ? 'result hasPhoto' : 'result'}>
                    <canvas
                      className="cameraCanvas"
                      ref={photoRef}
                      style={{ display: isDisabledSubmit ? 'block' : 'none' }}
                    ></canvas>
                    <canvas
                      className="cameraCanvas"
                      ref={frontPhotoRef}
                      style={{ display: !isDisabledSubmit ? 'block' : 'none' }}
                    ></canvas>
                    <canvas
                      className="cameraCanvas"
                      ref={backPhotoRef}
                      style={{
                        display: !isDisabledSubmit && isIdCard ? 'block' : 'none',
                        paddingTop: '15px',
                      }}
                    ></canvas>
                    {!isDisabledSubmit && (
                      <>
                        {frontImage && (
                          <RButton variant={'outline-warning'} className="mt-2" onClick={() => clearPhoto()}>
                            Clear {backImage && <>Front</>} Photo
                          </RButton>
                        )}
                        {backImage && (
                          <RButton variant={'outline-warning'} className="mt-2" onClick={() => clearPhoto(true)}>
                            Clear Back Photo
                          </RButton>
                        )}
                      </>
                    )}
                    {isIdCard && cameraLoaded && (
                      <>
                        {!frontImage && (
                          <>
                            <RButton variant={'info'} className="mt-2" onClick={() => keepPhoto(false)}>
                              Keep
                            </RButton>
                            <RButton variant={'outline-warning'} className="mt-2" onClick={() => retakePhoto(false)}>
                              Retake
                            </RButton>
                          </>
                        )}
                        {frontImage && !backImage && (
                          <>
                            <RButton variant={'info'} className="mt-2" onClick={() => keepPhoto(true)}>
                              Keep
                            </RButton>
                            <RButton variant={'outline-warning'} className="mt-2" onClick={() => retakePhoto(true)}>
                              Retake
                            </RButton>
                          </>
                        )}
                      </>
                    )}
                    {!isIdCard && cameraLoaded && (
                      <>
                        {!frontImage && (
                          <>
                            <RButton variant={'info'} className="mt-2" onClick={() => keepPhoto(false)}>
                              Keep
                            </RButton>
                            <RButton variant={'outline-warning'} className="mt-2" onClick={() => retakePhoto(false)}>
                              Retake
                            </RButton>
                          </>
                        )}
                      </>
                    )}
                    {!isDisabledSubmit && (
                      <RButton
                        variant={'primary'}
                        className="mt-2"
                        onClick={() => {
                          if (!isDisabledSubmit) {
                            handleWebcamUpload();
                          }
                        }}
                      >
                        Save document
                      </RButton>
                    )}
                  </div>
                </div>
              </BootstrapModal.Body>
              <BootstrapModal.Footer>
                <RButton variant={'secondary'} onClick={() => setOpenCameraModal(false)}>
                  Close
                </RButton>
              </BootstrapModal.Footer>
            </Modal>
          </>
        )}
        {cameraPermission && !cameraErrored && cameraExists && (
          <>
            <Button
              key={`button-${primary ? 'prim' : 'second'}`}
              className={'btn-btn btn-primary mb-4'}
              onClick={() => {
                setOpenCameraModal(!openCameraModal);
                setSelectedDocumentName(selectedDocumentName);
                if (!openCameraModal) getVideo();
              }}
            >
              Upload with Webcam/Camera
            </Button>
          </>
        )}

        {!cameraPermission && (
          <div className="mb-4">
            <strong>
              Webcam/Camera Permission has been denied. To upload with Webcam/Camera, please reset/allow the perimission
              and refresh the page.
            </strong>
          </div>
        )}
        {cameraErrored && (
          <div className="mb-4">
            <strong>
              Webcam/Camera has failed to load. Upload with Webcam/Camera is disabled. Sorry for the inconveneince.
            </strong>
          </div>
        )}
        <br />
        <span className="mute" style={{ marginTop: '1rem' }}>
          <Text id={'review-documents.files'} page="account" />
        </span>
      </>
      {errors.length > 0 && (
        <div className="errors">
          <ul>
            {errors.map((error, errorIndex) => (
              // if the error code is INVALID_DOCUMENT, apply some CSS to make the error more visible
              <li
                className="error"
                key={`error_${errorIndex}`}
                style={error.code === 'INVALID_DOCUMENT' ? { fontWeight: 'bold' } : {}}
              >
                {error.message}
              </li>
            ))}
          </ul>
        </div>
      )}
    </FormGroup>
  );
}
